import { CopyIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Field, FieldContent, FieldError, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { copyTextToClipboard } from '@/utils/clipboard';

function CopyFieldButton({
  label,
  value,
  disabled,
}: {
  label: string;
  value: string;
  disabled?: boolean;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon-sm"
      className="shrink-0"
      disabled={disabled || !value}
      title={`Copy ${label}`}
      onClick={() => void copyTextToClipboard(value, label)}
    >
      <CopyIcon />
    </Button>
  );
}

function FormFieldLabel({
  label,
  required,
  copyable,
  copyValue,
}: {
  label: string;
  required?: boolean;
  copyable?: boolean;
  copyValue: string;
}) {
  return (
    <div className="flex w-full items-center justify-between gap-2">
      <FieldLabel className="w-auto">
        {label}
        {required ? ' *' : ''}
      </FieldLabel>
      {copyable ? <CopyFieldButton label={label} value={copyValue} /> : null}
    </div>
  );
}

interface BaseFieldProps {
  label: string;
  error?: string;
  required?: boolean;
  className?: string;
}

interface TextFormFieldProps extends BaseFieldProps {
  type?: 'text' | 'email' | 'number' | 'date';
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  copyable?: boolean;
}

export function TextFormField({
  label,
  error,
  required,
  className,
  type = 'text',
  value,
  onChange,
  placeholder,
  disabled,
  copyable = false,
}: TextFormFieldProps) {
  return (
    <Field data-invalid={!!error} className={className}>
      {copyable ? (
        <FormFieldLabel label={label} required={required} copyable copyValue={value} />
      ) : (
        <FieldLabel>
          {label}
          {required ? ' *' : ''}
        </FieldLabel>
      )}
      <FieldContent>
        <Input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          aria-invalid={!!error}
        />
        {error ? <FieldError>{error}</FieldError> : null}
      </FieldContent>
    </Field>
  );
}

interface TextareaFormFieldProps extends BaseFieldProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
  disabled?: boolean;
}

export function TextareaFormField({
  label,
  error,
  required,
  className,
  value,
  onChange,
  placeholder,
  rows = 3,
  disabled,
}: TextareaFormFieldProps) {
  return (
    <Field data-invalid={!!error} className={className}>
      <FieldLabel>
        {label}
        {required ? ' *' : ''}
      </FieldLabel>
      <FieldContent>
        <Textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={rows}
          disabled={disabled}
          aria-invalid={!!error}
        />
        {error ? <FieldError>{error}</FieldError> : null}
      </FieldContent>
    </Field>
  );
}

export interface SelectOption {
  value: string;
  label: string;
}

interface SelectFormFieldProps extends BaseFieldProps {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  copyable?: boolean;
}

export function SelectFormField({
  label,
  error,
  required,
  className,
  value,
  onChange,
  options,
  placeholder,
  disabled,
  copyable = false,
}: SelectFormFieldProps) {
  const selectedLabel = options.find((opt) => opt.value === value)?.label;
  const copyValue = selectedLabel ?? value;

  return (
    <Field data-invalid={!!error} className={className}>
      {copyable ? (
        <FormFieldLabel label={label} required={required} copyable copyValue={copyValue} />
      ) : (
        <FieldLabel>
          {label}
          {required ? ' *' : ''}
        </FieldLabel>
      )}
      <FieldContent>
        <Select value={value} onValueChange={(v) => onChange(v ?? '')} disabled={disabled}>
          <SelectTrigger className="w-full bg-white">
            <SelectValue placeholder={placeholder}>{selectedLabel}</SelectValue>
          </SelectTrigger>
          <SelectContent alignItemWithTrigger={false} sideOffset={8}>
            {options.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {error ? <FieldError>{error}</FieldError> : null}
      </FieldContent>
    </Field>
  );
}

interface CustomFormFieldProps extends BaseFieldProps {
  children: ReactNode;
}

export function CustomFormField({
  label,
  error,
  required,
  className,
  children,
}: CustomFormFieldProps) {
  return (
    <Field data-invalid={!!error} className={cn(className)}>
      <FieldLabel>
        {label}
        {required ? ' *' : ''}
      </FieldLabel>
      <FieldContent>
        {children}
        {error ? <FieldError>{error}</FieldError> : null}
      </FieldContent>
    </Field>
  );
}
